// Centralised query keys + hooks. Keeping them in one place lets us:
//   - invalidate consistently from mutation side-effects (e.g. revoking a key
//     invalidates both the key list and the audit log).
//   - hit `prefetch` from route loaders without scattering string keys.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';

import {
  adminUsers,
  apiKeys,
  audit,
  auth,
  deliveries,
  gateways,
  merchants,
  mfa,
  reports,
  settlements,
  webhookEndpoints,
} from './client';
import type {
  AdminUser,
  ApiKeyCreate,
  ApiKeyUpdate,
  CreateAdminUserBody,
  Me,
  PaymentRow,
  PspCode,
  UpdateAdminUserBody,
  WebhookDeliveryStatus,
} from './types';

export const qk = {
  me: ['me'] as const,
  merchants: ['merchants'] as const,
  merchant: (id: string) => ['merchants', id] as const,
  apiKeys: (merchantId: string) => ['merchants', merchantId, 'api-keys'] as const,
  gateways: (merchantId: string) => ['merchants', merchantId, 'gateways'] as const,
  webhookEndpoints: (merchantId: string) =>
    ['merchants', merchantId, 'webhooks'] as const,
  deliveries: (status?: WebhookDeliveryStatus) =>
    ['deliveries', status ?? 'all'] as const,
  dashboard: (windowHours: number) => ['dashboard', windowHours] as const,
  payments: (filters: Record<string, unknown>) => ['payments', filters] as const,
  paymentDetail: (id: string) => ['payments', id] as const,
  audit: (filters: Record<string, unknown>) => ['audit', filters] as const,
  settlements: (filters: Record<string, unknown>) => ['settlements', filters] as const,
  settlement: (id: string) => ['settlements', id] as const,
  settlementRows: (id: string, filters: Record<string, unknown>) =>
    ['settlements', id, 'rows', filters] as const,
  adminUsers: ['admin-users'] as const,
};

// -----------------------------------------------------------------------------
// Auth
// -----------------------------------------------------------------------------

type MeOpts = Omit<UseQueryOptions<Me>, 'queryKey' | 'queryFn'>;

export function useMe(opts: MeOpts = {}) {
  return useQuery<Me>({
    queryKey: qk.me,
    queryFn: auth.me,
    retry: false,
    ...opts,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: auth.logout,
    onSuccess: () => qc.clear(),
  });
}

// -----------------------------------------------------------------------------
// MFA enrolment
// -----------------------------------------------------------------------------

export function useMfaEnrol() {
  return useMutation({ mutationFn: mfa.enrol });
}

export function useMfaConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => mfa.confirm(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

export function useMfaDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => mfa.disable(password),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

// -----------------------------------------------------------------------------
// Admin users (RBAC management)
// -----------------------------------------------------------------------------

export const useAdminUsers = () =>
  useQuery<AdminUser[]>({ queryKey: qk.adminUsers, queryFn: adminUsers.list });

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAdminUserBody) => adminUsers.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAdminUserBody }) =>
      adminUsers.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

export function useDisableAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminUsers.disable(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

export function useUnlockAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminUsers.unlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

// -----------------------------------------------------------------------------
// Merchants + API keys
// -----------------------------------------------------------------------------

export const useMerchants = () =>
  useQuery({ queryKey: qk.merchants, queryFn: merchants.list });

export const useMerchant = (id: string) =>
  useQuery({
    queryKey: qk.merchant(id),
    queryFn: () => merchants.get(id),
    enabled: !!id,
  });

export function useCreateMerchant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: merchants.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.merchants }),
  });
}

export function useUpdateMerchant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; name: string; status: string }) =>
      merchants.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.merchants });
      qc.invalidateQueries({ queryKey: qk.merchant(id) });
    },
  });
}

export function useDeleteMerchant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => merchants.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.merchants }),
  });
}

export const useApiKeys = (merchantId: string) =>
  useQuery({
    queryKey: qk.apiKeys(merchantId),
    queryFn: () => apiKeys.list(merchantId),
    enabled: !!merchantId,
  });

export function useCreateApiKey(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiKeyCreate) => apiKeys.create(merchantId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.apiKeys(merchantId) }),
  });
}

export function useUpdateApiKey(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, body }: { keyId: string; body: ApiKeyUpdate }) =>
      apiKeys.update(merchantId, keyId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.apiKeys(merchantId) }),
  });
}

export function useRevokeApiKey(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => apiKeys.revoke(merchantId, keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.apiKeys(merchantId) }),
  });
}

// -----------------------------------------------------------------------------
// Gateways
// -----------------------------------------------------------------------------

export const useGateways = (merchantId: string) =>
  useQuery({
    queryKey: qk.gateways(merchantId),
    queryFn: () => gateways.list(merchantId),
    enabled: !!merchantId,
  });

export function useUpsertGateway(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ psp, body }: { psp: PspCode; body: Parameters<typeof gateways.upsert>[2] }) =>
      gateways.upsert(merchantId, psp, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.gateways(merchantId) }),
  });
}

export function useTestGateway(merchantId: string) {
  return useMutation({
    mutationFn: (psp: PspCode) => gateways.test(merchantId, psp),
  });
}

// -----------------------------------------------------------------------------
// Webhook endpoints + deliveries
// -----------------------------------------------------------------------------

export const useWebhookEndpoints = (merchantId: string) =>
  useQuery({
    queryKey: qk.webhookEndpoints(merchantId),
    queryFn: () => webhookEndpoints.list(merchantId),
    enabled: !!merchantId,
  });

export function useCreateWebhookEndpoint(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof webhookEndpoints.create>[1]) =>
      webhookEndpoints.create(merchantId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.webhookEndpoints(merchantId) }),
  });
}

export function useDeactivateWebhookEndpoint(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webhookEndpoints.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.webhookEndpoints(merchantId) }),
  });
}

export const useDeliveries = (
  status?: WebhookDeliveryStatus,
  refetchMs = 0,
) =>
  useQuery({
    queryKey: qk.deliveries(status),
    queryFn: () => deliveries.list({ status, limit: 200 }),
    refetchInterval: refetchMs > 0 ? refetchMs : false,
  });

export const useDeliveryDetail = (id: string | null) =>
  useQuery({
    queryKey: ['delivery', id],
    queryFn: () => deliveries.get(id!),
    enabled: !!id,
  });

export function useReplayDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deliveries.replay(id),
    onSuccess: () =>
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'deliveries' }),
  });
}

export function useResolveDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deliveries.resolve(id),
    onSuccess: () =>
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'deliveries' }),
  });
}

// -----------------------------------------------------------------------------
// Reports
// -----------------------------------------------------------------------------

export const useDashboard = (windowHours = 24) =>
  useQuery({
    queryKey: qk.dashboard(windowHours),
    queryFn: () => reports.dashboard(windowHours),
    refetchInterval: 30_000,
  });

export const usePayments = (filters: Parameters<typeof reports.payments>[0] = {}) =>
  useQuery<PaymentRow[]>({
    queryKey: qk.payments(filters),
    queryFn: () => reports.payments(filters),
  });

export const usePaymentDetail = (id: string) =>
  useQuery({
    queryKey: qk.paymentDetail(id),
    queryFn: () => reports.paymentDetail(id),
    enabled: !!id,
  });

// -----------------------------------------------------------------------------
// Settlements
// -----------------------------------------------------------------------------

export const useSettlements = (filters: Parameters<typeof settlements.list>[0] = {}) =>
  useQuery({
    queryKey: qk.settlements(filters),
    queryFn: () => settlements.list(filters),
  });

export const useSettlement = (id: string) =>
  useQuery({
    queryKey: qk.settlement(id),
    queryFn: () => settlements.get(id),
    enabled: !!id,
  });

export const useSettlementRows = (
  id: string,
  filters: Parameters<typeof settlements.rows>[1] = {},
) =>
  useQuery({
    queryKey: qk.settlementRows(id, filters),
    queryFn: () => settlements.rows(id, filters),
    enabled: !!id,
  });

export function useUploadSettlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: settlements.upload,
    onSuccess: () =>
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'settlements' }),
  });
}

export const useSupportedSettlementSources = () =>
  useQuery({
    queryKey: ['settlements', 'sources'],
    queryFn: () => settlements.supportedSources(),
    staleTime: 60_000,
  });

export function useTriggerSettlementPull() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: settlements.pull,
    onSuccess: () =>
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'settlements' }),
  });
}

// -----------------------------------------------------------------------------
// Audit
// -----------------------------------------------------------------------------

export const useAudit = (filters: Parameters<typeof audit.list>[0] = {}) =>
  useQuery({
    queryKey: qk.audit(filters),
    queryFn: () => audit.list(filters),
  });
